import { useState, useEffect, useRef } from 'react';

interface NewsGridProps {
  selectedCategory: string;
  currentPage: number;
  onPageChange: (page: number) => void;
}

interface NewsArticle {
  id: number;
  title: string;
  category: string[];
  date: string;
  excerpt: string;
  image: string;
  readTime: string;
  author: string;
}

export default function NewsGrid({ selectedCategory, currentPage, onPageChange }: NewsGridProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const allArticles: NewsArticle[] = [
    {
      id: 1,
      title: "How Agentic AI is Revolutionizing Ad Campaign Optimization",
      category: ["AI & Automation Trends", "Digital Marketing Strategies"],
      date: "June 5, 2025",
      excerpt: "Explore the latest breakthroughs in AI agents that are autonomously planning, launching, and refining ad campaigns for unprecedented ROI. Learn how this tech is transforming performance marketing.",
      image: "/assets/ai-campaign-1.jpg",
      readTime: "8 min read",
      author: "Hash Adz Team"
    },
    {
      id: 2,
      title: "Hash Adz Celebrates a Decade of Digital Innovation from Kochi!",
      category: ["Hash Adz Updates"],
      date: "May 30, 2025",
      excerpt: "Join us in celebrating 10 incredible years of shaping digital futures from our home in Kochi, Kerala. A look back at our journey and what's next.",
      image: "/assets/anniversary-2.jpg",
      readTime: "5 min read",
      author: "Hash Adz Team"
    },
    {
      id: 3,
      title: "Beyond the Screen: The Power of Multi-Color 3D Printing in Brand Prototyping",
      category: ["Emerging Tech", "Branding & Design"],
      date: "May 28, 2025",
      excerpt: "Discover how vibrant, tangible 3D models are revolutionizing product design and brand visualization, offering a new dimension of creativity.",
      image: "/assets/3d-printing-3.jpg",
      readTime: "6 min read",
      author: "Design Team"
    },
    {
      id: 4,
      title: "The Future of Social Media: AI-Driven Content Creation and Engagement",
      category: ["AI & Automation Trends", "Digital Marketing Strategies"],
      date: "May 25, 2025",
      excerpt: "Discover how artificial intelligence is transforming social media marketing with automated content generation, predictive analytics, and personalized user experiences.",
      image: "/assets/social-ai-4.jpg",
      readTime: "7 min read",
      author: "Marketing Team"
    },
    {
      id: 5,
      title: "Web3 Brand Strategies: Building Communities in the Metaverse",
      category: ["Emerging Tech", "Branding & Design"],
      date: "May 22, 2025",
      excerpt: "Learn how forward-thinking brands are establishing meaningful connections and building loyal communities in virtual worlds and blockchain-based platforms.",
      image: "/assets/web3-brand-5.jpg",
      readTime: "9 min read",
      author: "Innovation Team"
    },
    {
      id: 6,
      title: "Data-Driven Design: How Analytics Shape Modern Brand Identities",
      category: ["Branding & Design", "Digital Marketing Strategies"],
      date: "May 20, 2025",
      excerpt: "Explore the intersection of creativity and data science in modern brand design, where user behavior insights drive visual identity decisions.",
      image: "/assets/data-design-6.jpg",
      readTime: "6 min read",
      author: "Design Team"
    }
  ];

  const filteredArticles = selectedCategory === 'All Insights' 
    ? allArticles 
    : allArticles.filter(article => article.category.includes(selectedCategory));

  const articlesPerPage = 6;
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    onPageChange(1);
  }, [selectedCategory]);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Latest Insights & Updates
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {filteredArticles.length} articles found in {selectedCategory}
          </p>
        </div>

        {/* Articles Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {currentArticles.map((article, index) => (
            <article
              key={article.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 overflow-hidden border border-gray-100 cursor-pointer"
              onMouseEnter={() => setHoveredCard(article.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Article Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Category Tags */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {article.category.map((cat, catIndex) => (
                    <span
                      key={catIndex}
                      className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-3 py-1 rounded-full font-medium"
                    >
                      {cat}
                    </span>
                  ))}
                </div>

                {/* Read Time */}
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {article.readTime}
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300 line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                  {article.excerpt}
                </p>

                <button className="group/btn inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-red-600 transition-colors duration-300 whitespace-nowrap cursor-pointer">
                  Read More
                  <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </button>
              </div>

              {/* Hover Effect Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-r from-orange-500/5 to-red-500/5 opacity-0 transition-opacity duration-300 ${
                hoveredCard === article.id ? 'opacity-100' : ''
              }`} />
            </article>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`flex justify-center items-center gap-2 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}